import { CodeBlock } from '@ui/pages/onboarding/components/configuration/CodeBlock'

import { ConfigurationProps } from './types'

export const AWSLambdaConfiguration = ({ instanceUrl }: ConfigurationProps) => (
  <>
    <h1 className="my-4 text-3xl font-bold">AWS Lambda Telemetry and Logging</h1>

    <p className="my-4 text-gray-500">
      Datable leverages Lambda Extensions to provide drop-in Log and Trace support for your Lambda functions. Add a
      layer to your Lambda function and configure a few environment variables to send telemetry.
    </p>

    <h2 className="my-4 text-2xl font-bold">How it works</h2>

    <p className="my-4 text-gray-500">
      The Datable Lambda Extension is a Lambda Layer that is added to your existing Lambda functions and leverages the
      AWS Lambda Telemetry API to provide telemetry and logging for your Lambda functions. The extension currently
      supports functions using a Node 14.x, 16.x, 18.x, or 20.x runtime.
    </p>

    <h2 className="my-4 text-2xl font-bold">Pricing</h2>

    <p className="my-4 text-gray-500">
      The Datable Lambda Extension runs on a layer of your existing Lambda functions. AWS charges for this additional
      layer is minimal as resources are coupled with the adjoining lambda and the extension only extends the runtime of
      the lambda by a few seconds to collect and send data following the lambda invocation. For more information on AWS
      Lambda Extension pricing, see the{' '}
      <a
        href="https://aws.amazon.com/lambda/pricing/"
        className="text-blue-500 underline hover:text-blue-700"
        target="_blank">
        AWS Lambda Pricing
      </a>{' '}
      page.
    </p>

    <h2 className="my-4 text-2xl font-bold">Onboarding a function</h2>

    <p className="my-4 text-gray-500">
      Attaching the Datable extension to your existing Lambda function is as simple as adding a layer and an environment
      variable.
    </p>

    <h3 className="my-4 text-xl font-bold">Adding a layer to an existing function</h3>

    <p className="my-4 text-gray-500">
      1. Navigate to an existing AWS Lambda function and select the <strong>Code</strong> tab.
    </p>
    <p className="my-4 text-gray-500">
      2. Scroll down to the <strong>Layers</strong> section and select <strong>Add a layer</strong>.
    </p>
    <p className="my-4 text-gray-500">
      3. Select <strong>Specify an ARN</strong> and input the relevant ARN from the list below:
    </p>

    <CodeBlock
      code={`US-EAST-1: arn:aws:lambda:us-east-1:075279486443:layer:datable-telemetry-extension-node:2
US-EAST-2: arn:aws:lambda:us-east-2:075279486443:layer:datable-telemetry-extension-node:2
US-WEST-1: arn:aws:lambda:us-west-1:075279486443:layer:datable-telemetry-extension-node:2
US-WEST-2: arn:aws:lambda:us-west-2:075279486443:layer:datable-telemetry-extension-node:2
EU-CENTRAL-1: arn:aws:lambda:eu-central-1:075279486443:layer:datable-telemetry-extension-node:2
EU-WEST-1: arn:aws:lambda:eu-west-1:075279486443:layer:datable-telemetry-extension-node:2
`}
    />

    <p className="my-4 text-gray-500">
      4. Configure the required Datable dispatch URL by adding the following environment variable to your Lambda
      function:
    </p>

    <CodeBlock
      code={`
DATABLE_DISPATCH_POST_URI: https://${instanceUrl}:4318
`}
    />

    <h3 className="my-4 text-xl font-bold">Additional configuration</h3>

    <p className="my-4 text-gray-500">See the following environment variables for additional configuration options:</p>

    <p className="my-4 text-gray-500">
      - <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">DATABLE_DISABLE_LOGS</code>: Set to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">true</code> to disable logs from your lambda
      flowing into Datable. Defaults to <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">false</code>.
    </p>
    <p className="my-4 text-gray-500">
      - <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">DATABLE_DISABLE_SPANS</code>: Set to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">true</code> to disable telemetry, such as
      execution start/stop and memory usage information, from your lambda flowing into Datable. Defaults to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">false</code>.
    </p>
    <p className="my-4 text-gray-500">
      - <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">DATABLE_DISPATCH_MIN_BATCH_SIZE</code>: Set to
      an integer value to specify the minimum number of telemetry events to dispatch records to Datable. All records
      will eventually be dispatched when lambdas are shutdown, but this can reduce total number of calls. Defaults to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">1</code>.
    </p>
    <p className="my-4 text-gray-500">
      - <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">DATABLE_DISABLE_EXTENSION_LOGGING</code>: Set
      to <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">true</code> to disable extension logging.
      Extension logging is not sent to Datable and is only present in Cloudwatch lambda logs. This can reduce
      unnecessary logging, but can make troubleshooting extension setup or issues difficult. Defaults to{' '}
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm">false</code>.
    </p>
  </>
)
